/**
 * Obstructions Service
 * API operations for obstruction management.
 *
 * Mirrors the shape of `notesService` / `tasksService`. Routes use the
 * canonical `/api/v1/obstructions` prefix declared in `API_ENDPOINTS`.
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  CreateObstructionRequest,
  UpdateObstructionRequest,
  UpdateObstructionStatusRequest,
  ObstructionFiltersParams,
  MapObstructionsParams,
} from '@/types/api/requests';
import type {
  ObstructionResponse,
  ObstructionListResponse,
  ObstructionMapResponse,
  ObstructionStatsResponse,
  UpdateObstructionApiResponse,
  UpdateObstructionStatusApiResponse,
} from '@/types/api/responses';

export const obstructionsService = {
  /**
   * Create a new obstruction.
   * Returns the persisted `ObstructionDto` (full DTO with relations).
   */
  create: (data: CreateObstructionRequest) =>
    apiClient.post<ObstructionResponse>(
      API_ENDPOINTS.OBSTRUCTIONS.BASE,
      data
    ),

  /**
   * Get all obstructions with optional filters and pagination.
   */
  getAll: (filters?: ObstructionFiltersParams) =>
    apiClient.get<ObstructionListResponse>(API_ENDPOINTS.OBSTRUCTIONS.BASE, {
      params: filters,
    }),

  /**
   * Get obstructions for map view, optionally within geographic bounds.
   */
  getForMap: (params: MapObstructionsParams) =>
    apiClient.get<ObstructionMapResponse[]>(API_ENDPOINTS.OBSTRUCTIONS.MAP, {
      params,
    }),

  /**
   * Get obstruction by ID with all relations.
   */
  getById: (id: string) =>
    apiClient.get<ObstructionResponse>(API_ENDPOINTS.OBSTRUCTIONS.BY_ID(id)),

  /**
   * Get obstruction by short ID (NanoID) with all relations.
   */
  getByShortId: (shortId: string) =>
    apiClient.get<ObstructionResponse>(
      API_ENDPOINTS.OBSTRUCTIONS.BY_SHORT_ID(shortId)
    ),

  /**
   * Update obstruction (partial — only supplied fields are applied).
   * Returns wrapped response with full obstruction DTO.
   */
  update: (id: string, data: UpdateObstructionRequest) =>
    apiClient.put<UpdateObstructionApiResponse>(
      API_ENDPOINTS.OBSTRUCTIONS.BY_ID(id),
      data
    ),

  /**
   * Update obstruction status only (partial update).
   */
  updateStatus: (id: string, data: UpdateObstructionStatusRequest) =>
    apiClient.patch<UpdateObstructionStatusApiResponse>(
      API_ENDPOINTS.OBSTRUCTIONS.STATUS(id),
      data
    ),

  /**
   * Delete obstruction by ID.
   */
  delete: (id: string) =>
    apiClient.delete<void>(API_ENDPOINTS.OBSTRUCTIONS.BY_ID(id)),

  /**
   * Get aggregated obstruction statistics for the dashboard / metadata view.
   */
  getStats: () =>
    apiClient.get<ObstructionStatsResponse>(API_ENDPOINTS.OBSTRUCTIONS.STATS),
};
